/**
 * Medidas da telemetria: rótulo, unidade, conversão e faixa normal (ISA-101) — Nexora Grid (frontend, US-402).
 *
 * Criado em: 16/10/2026 · Última modificação: 18/10/2026 · Versão: 1.4.0
 *
 * ISA-101: valor dentro da faixa fica em cinza; fora dela ganha a cor e a forma da prioridade (1 crítico a 4 baixo).
 * As faixas abaixo são as padrão da demonstração; a corrente nominal vem da ficha técnica do ativo (GUS-1102).
 * Pressão de recalque não é pressão da rede: a checagem da NBR 12218 fica na simulação (SCADA 3D) até haver sensores
 * na rede.
 * v1.1: medidas do robô da rede (pressão da rede, jusante/montante, altura da água, entrada/saída) e faixa de tensão
 * pela tensão nominal da ficha.
 * v1.2 (GUS-1106): motor trifásico do contrato v2 — fases R/S/T de corrente e tensão, fator de potência e desequilíbrio
 * (corrente ≤ 10%; tensão ≤ 3%, PRODIST Módulo 8).
 * v1.3 (GUS-1507): parâmetros do poço e do inversor informados pelo CLP (nível dinâmico/estático, corrente nominal).
 * v1.4 (D-009): pressão na rede e a jusante avaliadas pela NBR 12218 — abaixo de 10 mca (dinâmica mínima) e acima de
 *   40 mca (estática máxima). O alerta passou a coincidir com a faixa mostrada (antes alertava só acima de 50 mca).
 */

export type Prioridade = 1 | 2 | 3 | 4;
export type Medida = {
  rotulo: string; unidade: string; casas: number; converter?: (v: number) => number;
  avaliar?: (v: number, contexto: { correnteNominal?: number; tensaoNominal?: number }) => { prioridade: Prioridade; motivo: string } | null;
  faixa?: string;
};

/** NBR 12218 (rede de distribuição): pressão dinâmica mínima 10 mca e estática máxima 40 mca. */
export const NBR_12218 = { minima: 10, maxima: 40 } as const;

/** Pressão fora da NBR 12218: abaixo da mínima é prioridade 2 (falta de água); acima da máxima, 3 (risco de vazamento). */
function avaliarPressaoNbr(v: number): { prioridade: Prioridade; motivo: string } | null {
  if (v < NBR_12218.minima) return { prioridade: 2, motivo: `abaixo de ${NBR_12218.minima} mca (NBR 12218)` };
  if (v > NBR_12218.maxima) return { prioridade: 3, motivo: `acima de ${NBR_12218.maxima} mca (NBR 12218)` };
  return null;
}

export const MEDIDAS: Record<string, Medida> = {
  nivel_reservatorio_pct: {
    rotulo: "Nível do reservatório", unidade: "%", casas: 0, faixa: "30–95 %",
    avaliar: (v) => (v < 20 ? { prioridade: 1, motivo: "nível crítico (< 20%)" } : v < 30 ? { prioridade: 3, motivo: "nível baixo (< 30%)" }
      : v > 95 ? { prioridade: 3, motivo: "nível alto (> 95%)" } : null),
  },
  vazao_instantanea_m3h: { rotulo: "Vazão", unidade: "m³/h", casas: 1 },
  vazao_entrada_m3h: { rotulo: "Entrada", unidade: "m³/h", casas: 1 },
  vazao_saida_m3h: { rotulo: "Saída", unidade: "m³/h", casas: 1 },
  nivel_m: { rotulo: "Altura da água", unidade: "m", casas: 2 },
  pressao_rede_mca: {
    rotulo: "Pressão na rede", unidade: "mca", casas: 1, faixa: "10–40 mca (NBR 12218)",
    avaliar: avaliarPressaoNbr,
  },
  pressao_jusante_mca: {
    rotulo: "Pressão a jusante", unidade: "mca", casas: 1, faixa: "10–40 mca (NBR 12218)",
    avaliar: avaliarPressaoNbr,
  },
  pressao_montante_mca: { rotulo: "Pressão a montante", unidade: "mca", casas: 1 },
  // P-027: pressões que não são de rede (não seguem a NBR 12218)
  pressao_recalque_mca: { rotulo: "Pressão de recalque", unidade: "mca", casas: 1 },
  pressao_mca: { rotulo: "Pressão", unidade: "mca", casas: 1 },
  pressao_recalque_bar: { rotulo: "Pressão de recalque", unidade: "mca", casas: 1, converter: (v) => v * 10.197 },
  corrente_fase_a: {
    rotulo: "Corrente (fase A)", unidade: "A", casas: 1,
    avaliar: (v, c) => (c.correnteNominal && v > c.correnteNominal * 1.1 ? { prioridade: 2, motivo: "corrente > 110% da nominal" }
      : c.correnteNominal && v > c.correnteNominal ? { prioridade: 3, motivo: "corrente acima da nominal" } : null),
  },
  tensao_fase_v: {
    rotulo: "Tensão", unidade: "V", casas: 0, faixa: "±5% da nominal (ficha: 220 ou 380 V)",
    // nominal da ficha (tensao_v); sem ficha, o nível da própria medição (220 V ou 380 V)
    avaliar: (v, c) => {
      const n = c.tensaoNominal ?? (v > 300 ? 380 : 220);
      return Math.abs(v - n) > n * 0.1 ? { prioridade: 2, motivo: `tensão fora de ±10% de ${n} V` }
        : Math.abs(v - n) > n * 0.05 ? { prioridade: 3, motivo: `tensão fora de ±5% de ${n} V` } : null;
    },
  },
  potencia_ativa_kw: { rotulo: "Potência ativa", unidade: "kW", casas: 1 },
  fator_potencia: {
    rotulo: "Fator de potência", unidade: "", casas: 2, faixa: "≥ 0,92 (referência ANEEL)",
    avaliar: (v) => (v > 0 && v < 0.8 ? { prioridade: 4, motivo: "fator de potência abaixo de 0,80" } : null),
  },
  desequilibrio_corrente_pct: {
    rotulo: "Desequilíbrio de corrente", unidade: "%", casas: 1, faixa: "≤ 10 %",
    avaliar: (v) => (v > 20 ? { prioridade: 2, motivo: "desequilíbrio de corrente > 20%" } : v > 10 ? { prioridade: 3, motivo: "desequilíbrio de corrente > 10%" } : null),
  },
  desequilibrio_tensao_pct: {
    rotulo: "Desequilíbrio de tensão", unidade: "%", casas: 1, faixa: "≤ 3 % (PRODIST)",
    avaliar: (v) => (v > 3 ? { prioridade: 3, motivo: "desequilíbrio de tensão > 3% (PRODIST)" } : null),
  },
  frequencia_inversor_hz: {
    rotulo: "Frequência do inversor", unidade: "Hz", casas: 1, faixa: "55–61 Hz",
    avaliar: (v) => (v > 0 && (v < 55 || v > 61) ? { prioridade: 4, motivo: "frequência fora da faixa" } : null),
  },
  temperatura_bomba_c: {
    rotulo: "Temperatura do mancal", unidade: "°C", casas: 1, faixa: "≤ 75 °C",
    avaliar: (v) => (v > 95 ? { prioridade: 1, motivo: "temperatura > 95 °C" } : v > 85 ? { prioridade: 2, motivo: "temperatura > 85 °C" }
      : v > 75 ? { prioridade: 3, motivo: "temperatura > 75 °C" } : null),
  },
  volume_total_acumulado_m3: { rotulo: "Volume acumulado", unidade: "m³", casas: 0 },
  sinal_analogico_ma: { rotulo: "Sinal analógico", unidade: "mA", casas: 2 },
  nivel_dinamico_m: { rotulo: "Nível dinâmico (ND)", unidade: "m", casas: 1 },
  nivel_estatico_m: { rotulo: "Nível estático (NE)", unidade: "m", casas: 1 },
  corrente_nominal_a: { rotulo: "Corrente nominal (inversor)", unidade: "A", casas: 1 },
};

export const FORMA: Record<Prioridade, string> = { 1: "◆", 2: "▲", 3: "●", 4: "■" };
export const NOME_PRIORIDADE: Record<Prioridade, string> = { 1: "crítico", 2: "alto", 3: "médio", 4: "baixo" };

export function valorDe(medida: string, bruto: number): number {
  return MEDIDAS[medida]?.converter ? MEDIDAS[medida].converter!(bruto) : bruto;
}

export function fmtValor(medida: string, bruto: number): string {
  const m = MEDIDAS[medida];
  return valorDe(medida, bruto).toLocaleString("pt-BR", { maximumFractionDigits: m?.casas ?? 2, minimumFractionDigits: m?.casas ?? 0 });
}

export const nomeMedida = (medida: string) => MEDIDAS[medida]?.rotulo ?? medida.replace(/_/g, " ");
export const unidadeMedida = (medida: string) => MEDIDAS[medida]?.unidade ?? "";

// GUS-1106: fases R/S/T (= A/B/C) com a mesma avaliação da corrente e da tensão monofásicas do v1
for (const fase of ["r", "s", "t"]) {
  MEDIDAS[`corrente_fase_${fase}_a`] = { ...MEDIDAS.corrente_fase_a, rotulo: `Corrente (fase ${fase.toUpperCase()})` };
  MEDIDAS[`tensao_fase_${fase}_v`] = { ...MEDIDAS.tensao_fase_v, rotulo: `Tensão (fase ${fase.toUpperCase()})` };
}
